import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Science as ScienceIcon,
  LocalShipping as BottleIcon,
  RequestQuote as QuoteIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSearchData,
  selectSearchData,
  selectSearchStatus,
  selectSearchError,
  selectLastSearchParams,
} from "../../../redux/searchSlice";
import CommonLoader from "../common/CommonLoader";
import { NotificationManager } from "react-notifications";

// Results shown per type before the "Show Next 50 Results" button is needed
const PAGE_SIZE = 50;

const UniversalSearchPage = () => {
  const dispatch = useDispatch();

  const searchDataFromStore = useSelector(selectSearchData);
  const searchStatus = useSelector(selectSearchStatus);
  const searchError = useSelector(selectSearchError);
  const lastSearchParams = useSelector(selectLastSearchParams);

  const [search, setSearch] = useState("");
  // Number of results shown per type; grows by PAGE_SIZE with "Show Next" button
  const [visibleCounts, setVisibleCounts] = useState({});
  const [selectedTypes, setSelectedTypes] = useState(
    new Set(["Lab Order", "Invoice", "Bottle Order", "Quote"])
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Set default date range (2 months) on component mount
  useEffect(() => {
    const today = new Date();
    const twoMonthsAgo = new Date(today);
    twoMonthsAgo.setMonth(today.getMonth() - 2);

    const startDateValue = twoMonthsAgo.toISOString().split("T")[0];
    const endDateValue = today.toISOString().split("T")[0];

    setFromDate(startDateValue);
    setToDate(endDateValue);
  }, []);

  // Load initial data with default date range (only once)
  useEffect(() => {
    if (searchStatus === "idle" && fromDate && toDate) {
      dispatch(fetchSearchData({ fromDate, toDate }));
    }
  }, [dispatch, searchStatus, fromDate, toDate]);

  // Page heading uses the app's primary blue, same as other main screens
  const titleStyle = { color: "rgb(25,118,210)", fontWeight: "bold" };

  // Type configuration for icons, colors, and routes (routes = each type's edit screen)
  const typeConfig = {
    "Lab Order": {
      icon: <ScienceIcon />,
      color: "primary",
      route: "edit/lab-orders",
      bgColor: "#e3f2fd",
      label: "Lab Order",
      accentColor: "#1976d2",
      descriptionLabel: "Project",
    },
    Invoice: {
      icon: <ReceiptIcon />,
      color: "success",
      route: "invoices/edit",
      bgColor: "#e8f5e8",
      label: "Invoice",
      accentColor: "#2e7d32",
      descriptionLabel: "Lab Order",
    },
    "Bottle Order": {
      icon: <BottleIcon />,
      color: "warning",
      route: "bottle-orders/edit",
      bgColor: "#fff3e0",
      label: "Bottle Order",
      accentColor: "#ed6c02",
      descriptionLabel: "Description",
    },
    Quote: {
      icon: <QuoteIcon />,
      color: "secondary",
      route: "quotes",
      bgColor: "#f3e5f5",
      label: "Quote",
      accentColor: "#7b1fa2",
      descriptionLabel: "Description",
    },
  };

  // Get all available types from data
  const availableTypes = useMemo(() => {
    const types = new Set();
    if (searchDataFromStore) {
      searchDataFromStore.forEach((item) => types.add(item.type));
    }
    return Array.from(types).sort();
  }, [searchDataFromStore]);

  // Handle search button click - triggers API call
  const handleSearchClick = () => {
    if (fromDate === "" || toDate === "") {
      NotificationManager.error("Please select both From Date and To Date");
      return;
    }

    const searchParams = { fromDate, toDate };

    // Check if the same parameters were already searched to prevent duplicate API calls
    if (
      lastSearchParams &&
      lastSearchParams.fromDate === searchParams.fromDate &&
      lastSearchParams.toDate === searchParams.toDate
    ) {
      NotificationManager.info(
        "Data is already loaded for the selected date range"
      );
      return;
    }

    dispatch(fetchSearchData(searchParams));
  };

  // Filter and search functionality for each type (LOCAL SEARCH ONLY)
  const getFilteredDataByType = (type) => {
    let list = searchDataFromStore?.filter((item) => item.type === type) || [];

    // Filter by search term (LOCAL SEARCH)
    if (search.trim()) {
      const term = search.toLowerCase().trim();
      list = list.filter((item) => {
        const id = item.id.toLowerCase();
        const title = item.title.toLowerCase();
        const itemType = item.type.toLowerCase();
        const description = (item.description || "").toLowerCase();

        return (
          id.includes(term) ||
          title.includes(term) ||
          itemType.includes(term) ||
          description.includes(term)
        );
      });
    }

    return list;
  };

  // Start each type back at the first page when the filter or data changes
  useEffect(() => {
    setVisibleCounts({});
  }, [search, searchDataFromStore]);

  const handleShowNext = (type) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [type]: (prev[type] || PAGE_SIZE) + PAGE_SIZE,
    }));
  };

  // Handle type filter changes
  const handleTypeChange = (type, checked) => {
    const newSelectedTypes = new Set(selectedTypes);
    if (checked) {
      newSelectedTypes.add(type);
    } else {
      newSelectedTypes.delete(type);
    }
    setSelectedTypes(newSelectedTypes);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    setSelectedTypes(new Set(availableTypes));
  };

  const handleSelectNone = () => {
    setSelectedTypes(new Set());
  };

  // Handle item selection - opens the record's edit screen in a new tab
  const handleItemSelect = (item) => {
    const config = typeConfig[item.type];
    if (config) {
      const route = `/vlp/${config.route}/${encodeURIComponent(item.id)}`;
      window.open(route, "_blank");
    }
  };

  // Render individual type list
  const renderTypeList = (type) => {
    const config = typeConfig[type];
    const matchedData = getFilteredDataByType(type);
    // Render only the visible page(s) for performance
    const filteredData = matchedData.slice(0, visibleCounts[type] || PAGE_SIZE);
    const remainingCount = matchedData.length - filteredData.length;
    const totalCount =
      searchDataFromStore?.filter((item) => item.type === type).length || 0;

    if (!selectedTypes.has(type)) {
      return null;
    }

    return (
      <Grid item xs={12} md={6} lg={3} key={type}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            height: "600px",
            display: "flex",
            flexDirection: "column",
            border: `2px solid ${config?.accentColor}20`,
          }}
        >
          {/* Type Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: config?.bgColor || "grey.50",
              borderBottom: `2px solid ${config?.accentColor}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "white",
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                {React.cloneElement(config?.icon || <ScienceIcon />, {
                  sx: { color: config?.accentColor, fontSize: "1.5rem" },
                })}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {search.trim()
                    ? `${matchedData.length} of ${totalCount} results`
                    : `${totalCount} total items`}
                </Typography>
              </Box>
              <Chip
                label={matchedData.length}
                sx={{
                  bgcolor: config?.accentColor,
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              />
            </Box>
          </Box>

          {/* Type Items */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: config?.accentColor || "#ccc",
                borderRadius: "4px",
                opacity: 0.7,
                "&:hover": {
                  backgroundColor: config?.accentColor || "#999",
                  opacity: 1,
                },
              },
            }}
          >
            {searchStatus === "loading" ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress
                  size={40}
                  sx={{ color: config?.accentColor }}
                />
                <Typography variant="body2" color="text.secondary">
                  Loading {type.toLowerCase()}s...
                </Typography>
              </Box>
            ) : filteredData.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  px: 2,
                  color: "text.secondary",
                }}
              >
                {React.cloneElement(config?.icon, {
                  sx: { fontSize: 48, mb: 2, opacity: 0.3 },
                })}
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  {search.trim() ? "No results found" : "No items available"}
                </Typography>
                <Typography variant="body2">
                  {search.trim()
                    ? "Try different search terms"
                    : `No ${type.toLowerCase()}s to display`}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredData.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      disablePadding
                      sx={{
                        "&:hover": {
                          bgcolor: config?.bgColor + "60",
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleItemSelect(item)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: config?.bgColor + "80",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              bgcolor: config?.accentColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            }}
                          >
                            {React.cloneElement(config?.icon, {
                              sx: { color: "white", fontSize: "1.1rem" },
                            })}
                          </Box>
                        </ListItemIcon>

                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                lineHeight: 1.3,
                                mb: 0.3,
                              }}
                            >
                              {item.title}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontWeight: 400,
                              }}
                            >
                              ID: {item.id}
                              {item.description &&
                                ` | ${config?.descriptionLabel}: ${item.description}`}
                            </Typography>
                          }
                        />

                        <ArrowForwardIcon
                          sx={{
                            fontSize: "1.3rem",
                            color: config?.accentColor,
                            ml: 1,
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredData.length - 1 && (
                      <Divider variant="inset" component="li" sx={{ ml: 7 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}

            {searchStatus !== "loading" && remainingCount > 0 && (
              <Box
                sx={{
                  p: 1,
                  textAlign: "center",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  size="small"
                  onClick={() => handleShowNext(type)}
                  sx={{ color: config?.accentColor }}
                >
                  Show Next {Math.min(PAGE_SIZE, remainingCount)} Results
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    );
  };

  // Show main loader when initially loading data
  if (
    searchStatus === "loading" &&
    (!searchDataFromStore || searchDataFromStore.length === 0)
  ) {
    return (
      <div className="vlp-component-container">
        <span className="title" style={titleStyle}>
          Universal Search
        </span>
        <CommonLoader loading={true} />
      </div>
    );
  }

  return (
    <div className="vlp-component-container">
      {/* Header */}
      <span className="title" style={titleStyle}>
        Universal Search
      </span>

      {/* Date Range Filters with Search Button (Enter submits) */}
      <Paper
        elevation={1}
        sx={{ p: 2, borderRadius: 2, mt: 1, mb: 2 }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchClick();
          }}
          sx={{ display: "flex", gap: 2, alignItems: "end" }}
        >
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={searchStatus === "loading"}
          >
            {searchStatus === "loading" ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Search"
            )}
          </Button>
        </Box>
      </Paper>

      {searchError && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "error.lighter",
            border: "1px solid",
            borderColor: "error.light",
            borderRadius: 2,
          }}
        >
          <Typography color="error" variant="body1">
            {searchError}
          </Typography>
        </Paper>
      )}

      {/* Local Search and Filters Section */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        {/* Local Search Bar */}
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter results by ID, title, type, or description... (Local search)"
          fullWidth
          size="small"
          variant="outlined"
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1.5rem" }} color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Type Filters */}
        <Box sx={{ mb: 1 }}>
          <FormGroup row>
            {availableTypes.map((type) => {
              const config = typeConfig[type];
              if (!config) return null;

              const typeCount =
                searchDataFromStore?.filter((item) => item.type === type)
                  .length || 0;

              return (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedTypes.has(type)}
                      onChange={(e) =>
                        handleTypeChange(type, e.target.checked)
                      }
                      sx={{
                        color: config.accentColor,
                        "&.Mui-checked": {
                          color: config.accentColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {React.cloneElement(config.icon, {
                        sx: { fontSize: "1.3rem" },
                      })}
                      <Typography variant="body1" fontWeight={600}>
                        {config.label}
                      </Typography>
                      <Chip
                        label={typeCount}
                        sx={{
                          bgcolor: config.accentColor,
                          color: "white",
                          fontWeight: 600,
                          minWidth: "28px",
                          height: "24px",
                          fontSize: "0.8rem",
                        }}
                      />
                    </Box>
                  }
                  sx={{ mr: 4, mb: 1 }}
                />
              );
            })}
          </FormGroup>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Show All
          </Button>
          <Button size="small" variant="outlined" onClick={handleSelectNone}>
            Hide All
          </Button>
        </Box>
      </Paper>

      {/* Side by Side Lists */}
      {selectedTypes.size === 0 ? (
        <Paper
          elevation={1}
          sx={{
            p: 6,
            borderRadius: 3,
            textAlign: "center",
            bgcolor: "grey.25",
          }}
        >
          <SearchIcon sx={{ fontSize: 80, mb: 2, opacity: 0.2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
            Select categories to view
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Check the boxes above to show Lab Orders, Invoices, Bottle
            Orders, or Quotes
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {availableTypes.map((type) => renderTypeList(type))}
        </Grid>
      )}
    </div>
  );
};

export default UniversalSearchPage;
